import axiosInstance from './axiosInstance';
import { unAuthorizedAxios } from './Network';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  HiAttemptCreatePayload,
  HiAttemptCreateResponse,
  HiAttemptGetResponse,
  HiAttemptListResponse,
  HiAttemptUpdatePayload,
  HiAttemptUpdateResponse,
} from '@/pages/homeInspection/hiAttempt.schema';
import type { MediaResponse } from '@/types/media.types';
import type {
  HomeInspectionShareUpdateRequest,
  HomeInspectionShareUpdateResponse,
} from '@/types/homeInspection.types';
import type { GenericErrorResponse, InternalServerErrorResponse } from '@brainvaulttech/dgr-react-sdk';

export const uploadAttemptMedia = async (file: File): Promise<string> => {
  const { data: mediaData } = await axiosInstance.post<MediaResponse>('/media/', {
    fileName: file.name,
    mimeType: file.type,
    uploadedFor: 'WORK_ORDER_ATTEMPT',
  });

  if (!mediaData.presignedPost?.url) {
    throw new Error(`No upload URL received for ${file.name}`);
  }

  const formData = new FormData();
  Object.entries(mediaData.presignedPost.fields ?? {}).forEach(([key, value]) => {
    formData.append(key, String(value));
  });
  formData.append('file', file);

  await unAuthorizedAxios.post(mediaData.presignedPost.url, formData);

  if (!mediaData.media?.mediaId) {
    throw new Error(`No mediaId received for ${file.name}`);
  }

  return mediaData.media.mediaId;
};

export const createAttempt = async (
  workOrderId: string,
  payload: HiAttemptCreatePayload
): Promise<HiAttemptCreateResponse> => {
  const { data } = await axiosInstance.post<HiAttemptCreateResponse>(
    `/home-inspection/work-orders/${workOrderId}/attempts`,
    payload
  );
  return data;
};

export const getAttempt = async (
  workOrderId: string,
  attemptId: string
): Promise<HiAttemptGetResponse> => {
  const { data } = await axiosInstance.get<HiAttemptGetResponse>(
    `/home-inspection/work-orders/${workOrderId}/attempts/${attemptId}`
  );
  return data;
};

export const updateAttempt = async (
  workOrderId: string,
  attemptId: string,
  payload: HiAttemptUpdatePayload
): Promise<HiAttemptUpdateResponse> => {
  const { data } = await axiosInstance.patch<HiAttemptUpdateResponse>(
    `/home-inspection/work-orders/${workOrderId}/attempts/${attemptId}`,
    payload
  );
  return data;
};

export const getAttempts = async (workOrderId: string): Promise<HiAttemptListResponse> => {
  const { data } = await axiosInstance.get<HiAttemptListResponse>(
    `/home-inspection/work-orders/${workOrderId}/attempts`
  );
  return data;
};

export const useHIAttempts = (workOrderId: string | null) => {
  return useQuery<HiAttemptGetResponse[]>({
    queryKey: ['hi-attempts', workOrderId],
    queryFn: async () => {
      const { attempts } = await getAttempts(workOrderId!);
      return attempts;
    },
    enabled: workOrderId !== null,
  });
};

export const shareAttemptUpdates = async (
  workOrderId: string,
  body: HomeInspectionShareUpdateRequest
): Promise<HomeInspectionShareUpdateResponse> => {
  const { data } = await axiosInstance.post<HomeInspectionShareUpdateResponse>(
    `/home-inspection/work-orders/${workOrderId}/attempts/share-update`,
    body
  );
  return data;
};

export const useShareAttemptUpdates = () => {
  return useMutation<
    HomeInspectionShareUpdateResponse,
    GenericErrorResponse | InternalServerErrorResponse | undefined,
    { workOrderId: string; body: HomeInspectionShareUpdateRequest }
  >({
    mutationFn: async ({ workOrderId, body }) => shareAttemptUpdates(workOrderId, body),
  });
};
